(() => {
  // Native Shopify presentation context only. Access is still checked by the reader gates.
  const contextWorlds = new Set(['money-business', 'creator-marketing', 'ai-technology', 'confidence-communication', 'life-health-relationships']);
  const contextualResourceUrl = (item) => {
    const href = typeof item === 'string' ? item : item.url;
    const world = document.body?.dataset.syWorld;
    if (!href || !contextWorlds.has(world)) return href;
    try {
      const url = new URL(href, location.origin);
      if (url.origin !== location.origin) return href;
      const product = url.pathname.match(/^(?:\/collections\/[^/]+)?\/products\/([^/]+)\/?$/);
      if (product) {
        // Search results provide tags; do not paint a foreign resource as this World.
        const tags = typeof item === 'object' && Array.isArray(item.tags) ? item.tags : [];
        if (!tags.includes(`world:${world}`)) return href;
        url.pathname = `/collections/suityou-${world}/products/${product[1]}`;
        return url.pathname + url.search + url.hash;
      }
      const collection = url.pathname.match(/^\/collections\/([^/]+)\/?$/);
      if (collection) {
        if (typeof item === 'object' && !/^suityou-(ebooks-guides|audiobooks|video-courses|templates-tools)$/.test(collection[1])) {
          const knownInWorld = [...document.querySelectorAll('a[href]')].some((link) => {
            const match = link.getAttribute('href')?.match(/^\/collections\/([^/]+)\/world-([^/?#]+)/);
            return match && match[1] === collection[1] && match[2] === world;
          });
          if (!knownInWorld) return href;
        }
        url.pathname = `/collections/${collection[1]}/world-${world}`;
        return url.pathname + url.search + url.hash;
      }
    } catch (_) { /* Keep malformed/non-store links unchanged. */ }
    return href;
  };
  // Menu-managed links and their later mobile clones retain the current World too.
  document.querySelectorAll('.sy-platform-header a[href], .sy-platform-footer a[href], .sy-mobile-drawer a[href]').forEach((link) => {
    const href = link.getAttribute('href');
    if (href && /^\/collections\/[^/?#]+(?:[?#].*)?$/.test(href)) link.href = contextualResourceUrl(href);
  });

  const SELECTOR_FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const iconMarkup = (type) => {
    const icons = {
      product: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 4.5A2.5 2.5 0 0 1 7.5 2H19v17H7.5A2.5 2.5 0 0 0 5 21.5v-17Z"/><path d="M5 19h14"/></svg>',
      collection: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18"/></svg>',
      article: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3h9l4 4v14H6z"/><path d="M14 3v5h5M9 12h6M9 16h6"/></svg>',
      page: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3h9l4 4v14H6z"/><path d="M14 3v5h5"/></svg>'
    };
    return icons[type] || icons.page;
  };

  const productFormat = (item) => {
    const tags = Array.isArray(item.tags) ? item.tags : [];
    const formatTag = tags.find((tag) => String(tag).toLowerCase().startsWith('format:'));
    if (!formatTag) return item.type || 'Resource';
    const format = formatTag.split(':').slice(1).join(':').replace(/-/g, ' ');
    return format.replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const searchWorlds = ['Money & Business', 'Creator & Marketing', 'AI & Technology', 'Confidence & Communication', 'Life, Health & Relationships'];
  const worldPageHandles = ['money-business', 'creator-marketing', 'ai-technology', 'mindset-communication', 'life-health-confidence'];
  const searchCache = new Map();
  const normalizeSearch = (value) => String(value || '').toLowerCase().normalize('NFKD').replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const isWorldPage = (item) => {
    const path = new URL(item.url, location.origin).pathname;
    return path.startsWith('/pages/') && worldPageHandles.includes(path.split('/').pop());
  };

  const resultMeta = (type, item) => {
    if (type === 'products') return productFormat(item);
    if (type === 'collections') return 'World or topic';
    if (type === 'articles') {
      const tags = Array.isArray(item.tags) ? item.tags : [];
      const world = tags.find(tag => searchWorlds.includes(tag));
      const topic = tags.find(tag => tag !== world && !String(tag).includes(':') && /[A-Z]/.test(tag));
      return [world, topic].filter(Boolean).join(' · ') || 'Insight';
    }
    if (type === 'pages' && isWorldPage(item)) return 'World';
    if (type === 'pages' && /^\/blogs\/[^/]+$/.test(new URL(item.url, location.origin).pathname)) return 'Insights hub';
    return 'Page';
  };

  const resultImage = (item) => {
    if (typeof item.image === 'string' && item.image) return item.image;
    if (item.image && item.image.url) return item.image.url;
    if (item.featured_image && item.featured_image.url) return item.featured_image.url;
    return '';
  };

  const currentSearchResult = (item, type) => {
    if (!item.url || !item.title) return false;
    const url = new URL(item.url, location.origin);
    if (url.origin !== location.origin || /(?:^|\/)(?:qa|test|demo|development|internal|owner-preview|preview|backup)(?:[-/]|$)/i.test(url.pathname)) return false;
    if ((item.tags || []).some(tag => /^(?:qa|test|development|internal|owner-preview|rejected|draft|needs-revision)(?::|$)/i.test(String(tag)))) return false;
    if (type === 'products') return (item.tags || []).some(tag => String(tag).startsWith('format:'));
    if (type === 'articles') {
      const published = Date.parse(item.published_at);
      return /^\/blogs\/[^/]+\/[^/]+$/.test(url.pathname) && Number.isFinite(published) && published <= Date.now();
    }
    if (type === 'collections') {
      const handle = new URL(item.url, location.origin).pathname.split('/').filter(Boolean).pop();
      return !['membership', 'suityou-other-products-needs-review'].includes(handle);
    }
    return true;
  };

  const searchScore = (item, query) => {
    const phrase = normalizeSearch(query);
    const title = normalizeSearch(item.title);
    const handle = normalizeSearch(item.handle || new URL(item.url, location.origin).pathname.split('/').pop());
    if (title === phrase) return 1000;
    if (title.includes(phrase)) return 800 + phrase.length / title.length;
    if (handle.includes(phrase)) return 750;
    const words = phrase.split(' ');
    const titleWords = title.split(' ');
    const matches = words.filter(word => titleWords.some(candidate => candidate.startsWith(word))).length;
    const tags = normalizeSearch((item.tags || []).join(' '));
    return (matches === words.length ? 300 : 0) + matches / words.length * 200 + (tags.includes(phrase) ? 160 : 0);
  };

  const searchGroups = (data, query) => {
    const source = data?.resources?.results || {};
    const groups = [
      { key: 'articles', label: 'Insights', limit: 4, items: source.articles || [] },
      { key: 'products', label: 'Resources', limit: 4, items: source.products || [] },
      { key: 'collections', label: 'Worlds & Topics', limit: 3, items: [...(source.collections || []), ...(source.pages || []).filter(item => item.url && isWorldPage(item))] },
      { key: 'pages', label: 'Pages', limit: 3, items: (source.pages || []).filter(item => item.url && !isWorldPage(item)) }
    ];
    return groups.map(group => {
      const seen = new Set();
      const items = group.items.filter(item => {
        if (!item.url) return false;
        const type = group.key === 'collections' && new URL(item.url, location.origin).pathname.includes('/pages/') ? 'pages' : group.key;
        const path = new URL(item.url, location.origin).pathname;
        if (!currentSearchResult(item, type) || seen.has(path)) return false;
        seen.add(path);
        return true;
      }).sort((a, b) => searchScore(b, query) - searchScore(a, query)).slice(0, group.limit);
      return { ...group, items, score: items.length ? searchScore(items[0], query) : -1 };
    }).filter(group => group.items.length).sort((a, b) => b.score - a.score);
  };

  const createResult = (item, type, index, input) => {
    const link = document.createElement('a');
    link.className = 'sy-search-result';
    link.href = contextualResourceUrl(item);
    link.id = `${input.id}-option-${index}`;
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');
    link.dataset.syResultType = type;

    const media = document.createElement('span');
    media.className = 'sy-search-result__media';
    const imageUrl = resultImage(item);
    if (imageUrl) {
      const image = document.createElement('img');
      const thumbnailUrl = new URL(imageUrl, location.origin);
      if (thumbnailUrl.hostname === 'cdn.shopify.com') thumbnailUrl.searchParams.set('width', '128');
      image.src = thumbnailUrl.href;
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';
      if (type === 'articles') media.classList.add('sy-search-result__media--insight');
      image.addEventListener('error', () => { media.innerHTML = iconMarkup(type.slice(0, -1)); }, { once: true });
      media.appendChild(image);
    } else {
      media.innerHTML = iconMarkup(type.slice(0, -1));
    }

    const copy = document.createElement('span');
    copy.className = 'sy-search-result__copy';
    const title = document.createElement('span');
    title.className = 'sy-search-result__title';
    title.textContent = item.title || 'Untitled';
    const meta = document.createElement('span');
    meta.className = 'sy-search-result__meta';
    meta.textContent = resultMeta(type, item);
    copy.append(title, meta);

    link.append(media, copy);
    return link;
  };

  const initializeSearch = (root, header) => {
    if (!root || root.dataset.ready === 'true') return;
    root.dataset.ready = 'true';

    const context = root.dataset.sySearchContext;
    const launcher = root.querySelector('[data-sy-search-launch]');
    const surface = root.querySelector('[data-sy-search-surface]');
    const closeButton = root.querySelector('[data-sy-search-close]');
    const form = root.querySelector('[data-sy-search-form]');
    const input = root.querySelector('[data-sy-search-input]');
    const results = root.querySelector('[data-sy-search-results]');
    const hint = root.querySelector('[data-sy-search-hint]');
    if (!surface || !form || !input || !results || !hint) return;
    const mobileDrawer = context === 'mobile' ? root.closest('[data-sy-mobile-drawer]') : null;
    const setMobileSearchState = (active) => mobileDrawer?.classList.toggle('is-searching', active);

    let timer = null;
    let controller = null;
    let activeIndex = -1;
    let resultLinks = [];
    let lastOpener = null;

    const setPanelPosition = () => {
      if (context !== 'desktop') return;
      const rect = header.getBoundingClientRect();
      surface.style.top = `${Math.round(rect.bottom + 8)}px`;
    };

    const open = (opener = launcher) => {
      lastOpener = opener || document.activeElement;
      setPanelPosition();
      surface.hidden = false;
      launcher?.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => input.focus());
    };

    const clearActive = () => {
      activeIndex = -1;
      resultLinks.forEach((link) => {
        link.classList.remove('is-active');
        link.setAttribute('aria-selected', 'false');
      });
      input.removeAttribute('aria-activedescendant');
    };

    const close = (restoreFocus = false) => {
      clearTimeout(timer);
      controller?.abort();
      clearActive();
      resultLinks = [];
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      setMobileSearchState(false);
      if (context === 'desktop') {
        surface.hidden = true;
        launcher?.setAttribute('aria-expanded', 'false');
      }
      if (restoreFocus && lastOpener instanceof HTMLElement) lastOpener.focus();
    };

    const setActive = (nextIndex) => {
      if (!resultLinks.length) return;
      activeIndex = (nextIndex + resultLinks.length) % resultLinks.length;
      resultLinks.forEach((link, index) => {
        const active = index === activeIndex;
        link.classList.toggle('is-active', active);
        link.setAttribute('aria-selected', String(active));
      });
      const active = resultLinks[activeIndex];
      input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    };

    const makeGroup = (label, items, type, runningIndex) => {
      const group = document.createElement('section');
      group.className = 'sy-search-group';
      const heading = document.createElement('h3');
      heading.className = 'sy-search-group__title';
      heading.textContent = label;
      heading.id = `${input.id}-${type}-heading`;
      group.setAttribute('role', 'group');
      group.setAttribute('aria-labelledby', heading.id);
      group.appendChild(heading);
      items.forEach((item, offset) => group.appendChild(createResult(item, type, runningIndex + offset, input)));
      return group;
    };

    const renderEmpty = (query) => {
      results.replaceChildren();
      const empty = document.createElement('div');
      empty.className = 'sy-search-empty';
      const title = document.createElement('strong');
      title.textContent = `No results found for “${query}”`;
      const message = document.createElement('p');
      message.textContent = 'Try another topic, explore the five worlds, or let the Skill Finder guide you.';
      const links = document.createElement('div');
      links.className = 'sy-search-empty__links';
      const worlds = document.createElement('a');
      worlds.href = root.dataset.syWorldsLink || '/#choose-your-world';
      worlds.textContent = 'Explore the 5 Worlds';
      const finder = document.createElement('a');
      finder.href = root.dataset.sySkillLink || '/#skill-finder-preview';
      finder.textContent = 'Take the Skill Finder';
      links.append(worlds, finder);
      empty.append(title, message, links);
      results.appendChild(empty);
      resultLinks = [...results.querySelectorAll('a')];
      resultLinks.forEach((link, index) => {
        link.id = `${input.id}-option-${index}`;
        link.setAttribute('role', 'option');
        link.setAttribute('aria-selected', 'false');
      });
      clearActive();
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      setMobileSearchState(true);
      hint.hidden = true;
    };

    const render = (data, query) => {
      results.replaceChildren();
      let runningIndex = 0;
      // Shopify predictive search omits blogs; reuse the actual public Insights navigation destination.
      const insightsLink = header.querySelector('a[data-nav-id="insights"]');
      const insightsTitle = insightsLink?.textContent.trim();
      const nativeResults = data?.resources?.results || {};
      const navigationPages = insightsTitle && normalizeSearch(insightsTitle).includes(normalizeSearch(query))
        ? [{ title: insightsTitle, url: insightsLink.getAttribute('href') }]
        : [];
      const searchData = { resources: { results: { ...nativeResults, pages: [...(nativeResults.pages || []), ...navigationPages] } } };
      searchGroups(searchData, query).forEach(({ key, label, items }) => {
        results.appendChild(makeGroup(label, items, key, runningIndex));
        runningIndex += items.length;
      });

      if (!runningIndex) {
        renderEmpty(query);
        return;
      }

      const viewAll = document.createElement('a');
      viewAll.className = 'sy-search-view-all';
      viewAll.id = `${input.id}-option-${runningIndex}`;
      viewAll.setAttribute('role', 'option');
      viewAll.setAttribute('aria-selected', 'false');
      const viewAllUrl = new URL(form.action, location.origin);
      viewAllUrl.searchParams.set('q', query);
      viewAll.href = viewAllUrl.href;
      viewAll.textContent = `View all results for “${query}”`;
      results.appendChild(viewAll);
      resultLinks = [...results.querySelectorAll('.sy-search-result, .sy-search-view-all')];
      clearActive();
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      setMobileSearchState(true);
      hint.hidden = true;
    };

    const runSearch = async (query) => {
      const cacheKey = query.toLowerCase();
      const cached = searchCache.get(cacheKey);
      if (cached && Date.now() - cached.at < 60000) {
        render(cached.data, query);
        return;
      }

      controller?.abort();
      controller = new AbortController();
      const requestController = controller;
      root.classList.add('is-loading');
      hint.hidden = false;
      hint.textContent = `Searching SY Learning Platform for “${query}”…`;

      const url = new URL(`${new URL(form.action).pathname}/suggest.json`, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,collection,page,article');
      url.searchParams.set('resources[limit]', '8');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][fields]', 'title,body,tag');
      url.searchParams.set('resources[options][unavailable_products]', 'last');

      try {
        const response = await fetch(url.toString(), {
          signal: requestController.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
        const data = await response.json();
        if (requestController.signal.aborted || input.value.trim() !== query) return;
        if (searchCache.size >= 30) searchCache.delete(searchCache.keys().next().value);
        searchCache.set(cacheKey, { data, at: Date.now() });
        render(data, query);
      } catch (error) {
        if (error.name !== 'AbortError' && !requestController.signal.aborted && input.value.trim() === query) {
          renderEmpty(query);
          results.querySelector('strong').textContent = 'Search suggestions are temporarily unavailable.';
          const fallback = document.createElement('a');
          const url = new URL(form.action, location.origin); url.searchParams.set('q', query);
          fallback.href = url.href; fallback.textContent = 'View search results';
          fallback.id = `${input.id}-fallback`;
          fallback.setAttribute('role', 'option');
          fallback.setAttribute('aria-selected', 'false');
          results.querySelector('.sy-search-empty__links').prepend(fallback);
          resultLinks = [...results.querySelectorAll('a')];
        }
      } finally {
        if (controller === requestController) root.classList.remove('is-loading');
      }
    };

    const queueSearch = () => {
      const query = input.value.trim();
      clearTimeout(timer);
      controller?.abort();
      clearActive();
      resultLinks = [];
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      if (query.length < 2) {
        controller?.abort();
        results.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        setMobileSearchState(false);
        hint.hidden = false;
        hint.textContent = 'Type at least 2 characters to search resources, topics, insights, and pages.';
        return;
      }
      timer = window.setTimeout(() => runSearch(query), 200);
    };

    launcher?.addEventListener('click', () => open(launcher));
    closeButton?.addEventListener('click', () => close(true));
    input.addEventListener('input', queueSearch);
    input.addEventListener('focus', () => {
      if (context === 'desktop' && surface.hidden) open(input);
      if (input.value.trim().length >= 2 && resultLinks.length) {
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
    });

    form.addEventListener('submit', (event) => {
      const query = input.value.trim();
      if (!query) event.preventDefault();
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' && !results.hidden && resultLinks.length) {
        event.preventDefault();
        setActive(activeIndex + 1);
      } else if (event.key === 'ArrowUp' && !results.hidden && resultLinks.length) {
        event.preventDefault();
        setActive(activeIndex - 1);
      } else if (event.key === 'Enter' && event.target === input && !results.hidden && activeIndex >= 0) {
        event.preventDefault();
        resultLinks[activeIndex].click();
      } else if (event.key === 'Escape') {
        if (context === 'desktop') {
          event.stopPropagation();
          close(true);
        } else if (input.value || !results.hidden) {
          event.stopPropagation();
          clearTimeout(timer);
          controller?.abort();
          input.value = '';
          clearActive();
          results.hidden = true;
          input.setAttribute('aria-expanded', 'false');
          setMobileSearchState(false);
          hint.hidden = false;
          hint.textContent = 'Type at least 2 characters to search resources, topics, insights, and pages.';
          input.focus();
        }
      }
    });

    document.addEventListener('pointerdown', (event) => {
      if (context === 'desktop' && !root.contains(event.target) && !surface.hidden) close(false);
    });

    root.sySearchOpen = open;
    root.sySearchClose = close;
  };

  const populateMobileNavigation = (header, drawer) => {
    if (!header || !drawer || drawer.dataset.sySharedReady === 'true') return;

    const mobileWorlds = drawer.querySelector('[data-sy-mobile-worlds]');
    const sourceWorlds = header.querySelectorAll('.sy-platform-mega__worlds > .sy-platform-world');
    sourceWorlds.forEach((source) => {
      const card = source.cloneNode(true);
      card.classList.add('sy-mobile-world-card');
      card.querySelectorAll('li:nth-child(n + 5)').forEach((item) => item.remove());
      const count = card.querySelector('.sy-platform-world__count');
      const titleWrap = card.querySelector('.sy-platform-world__head > span:last-child');
      if (count && titleWrap) titleWrap.appendChild(count);
      const explore = card.querySelector('.sy-platform-world__more');
      if (explore) explore.textContent = 'Explore';
      mobileWorlds?.appendChild(card);
    });

    const promoMedia = drawer.querySelector('[data-sy-mobile-promo-media]');
    const promoImage = header.querySelector('.sy-platform-mega-promo > img')?.cloneNode(true);
    if (promoMedia && promoImage) {
      promoImage.alt = '';
      promoImage.setAttribute('aria-hidden', 'true');
      promoMedia.appendChild(promoImage);
    }

    const cloneRows = (sourceSelector, targetSelector, limit, className) => {
      const target = drawer.querySelector(targetSelector);
      if (!target) return;
      [...header.querySelectorAll(sourceSelector)].slice(0, limit).forEach((source) => {
        const row = source.cloneNode(true);
        row.classList.add(className);
        target.appendChild(row);
      });
    };

    cloneRows('[aria-labelledby^="SuitYouLatestInsights-"] .sy-platform-mega-story', '[data-sy-mobile-insights]', 2, 'sy-mobile-article-row');
    cloneRows('[aria-labelledby^="SuitYouTransformations-"] .sy-platform-mega-proof', '[data-sy-mobile-stories]', 2, 'sy-mobile-story-row');

    const quickTarget = drawer.querySelector('[data-sy-mobile-quick-links]');
    [...header.querySelectorAll('.sy-platform-mega-card--quick .sy-platform-quick-links > a')].forEach((source) => {
      const link = source.cloneNode(true);
      link.classList.add('sy-mobile-quick-link');
      link.querySelector('small')?.remove();
      link.querySelector('b')?.remove();
      quickTarget?.appendChild(link);
    });

    drawer.dataset.sySharedReady = 'true';
  };

  const initializeHeader = (header) => {
    if (!header || header.dataset.syReady === 'true') return;
    header.dataset.syReady = 'true';

    const menuItems = [...header.querySelectorAll('[data-sy-menu-item]')];
    const panels = [...header.querySelectorAll('[data-sy-menu-panel]')];
    const desktopSearch = header.querySelector('[data-sy-search-context="desktop"]');
    const mobileOpen = header.querySelector('[data-sy-mobile-open]');
    const mobileSearchOpen = header.querySelector('[data-sy-mobile-search]');
    const drawerId = mobileOpen?.getAttribute('aria-controls');
    const drawer = drawerId ? document.getElementById(drawerId) : null;
    const overlay = document.querySelector(`[data-sy-mobile-overlay="${header.id}"]`);
    const mobileClose = drawer?.querySelector('[data-sy-mobile-close]');
    const mobileSearch = drawer?.querySelector('[data-sy-search-context="mobile"]');
    let closeTimer = null;
    let drawerOpener = null;

    populateMobileNavigation(header, drawer);
    header.querySelectorAll('[data-sy-search]').forEach((search) => initializeSearch(search, header));
    drawer?.querySelectorAll('[data-sy-search]').forEach((search) => initializeSearch(search, header));

    const setScrolled = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    setScrolled();
    window.addEventListener('scroll', setScrolled, { passive: true });

    const closeMenus = () => {
      panels.forEach((panel) => {
        panel.classList.remove('is-open');
        panel.setAttribute('aria-hidden', 'true');
      });
      menuItems.forEach((item) => {
        delete item.dataset.lockedOpen;
        item.querySelector('[data-sy-menu-trigger]')?.setAttribute('aria-expanded', 'false');
      });
    };

    const positionPanel = (item, panel) => {
      const headerRect = header.getBoundingClientRect();
      panel.style.top = `${Math.round(headerRect.bottom + 8)}px`;
      if (panel.classList.contains('sy-platform-mega')) {
        panel.style.left = '50vw';
        return;
      }
      const itemRect = item.getBoundingClientRect();
      const panelWidth = panel.getBoundingClientRect().width || 250;
      const center = Math.max(panelWidth / 2 + 16, Math.min(window.innerWidth - panelWidth / 2 - 16, itemRect.left + itemRect.width / 2));
      panel.style.left = `${center}px`;
    };

    const openMenu = (item, locked = false) => {
      const trigger = item.querySelector('[data-sy-menu-trigger]');
      const panel = item.querySelector('[data-sy-menu-panel]');
      if (!trigger || !panel) return;
      clearTimeout(closeTimer);
      desktopSearch?.sySearchClose?.(false);
      panels.forEach((other) => {
        if (other !== panel) {
          other.classList.remove('is-open');
          other.setAttribute('aria-hidden', 'true');
        }
      });
      menuItems.forEach((other) => {
        if (other !== item) {
          delete other.dataset.lockedOpen;
          other.querySelector('[data-sy-menu-trigger]')?.setAttribute('aria-expanded', 'false');
        }
      });
      if (locked) item.dataset.lockedOpen = 'true';
      positionPanel(item, panel);
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
    };

    const scheduleClose = () => {
      if (menuItems.some((item) => item.dataset.lockedOpen === 'true')) return;
      clearTimeout(closeTimer);
      closeTimer = window.setTimeout(closeMenus, 180);
    };

    menuItems.forEach((item) => {
      const trigger = item.querySelector('[data-sy-menu-trigger]');
      const panel = item.querySelector('[data-sy-menu-panel]');
      if (!trigger || !panel) return;
      item.addEventListener('mouseenter', () => openMenu(item));
      item.addEventListener('mouseleave', scheduleClose);
      panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      panel.addEventListener('mouseleave', scheduleClose);
      trigger.addEventListener('click', () => {
        const openAndLocked = trigger.getAttribute('aria-expanded') === 'true' && item.dataset.lockedOpen === 'true';
        if (openAndLocked) closeMenus();
        else openMenu(item, true);
      });
      [trigger, panel].forEach((element) => element.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeMenus();
          trigger.focus();
        }
      }));
    });

    const openDrawer = (opener, focusSearch = false) => {
      if (!drawer) return;
      drawerOpener = opener || mobileOpen;
      drawer.inert = false;
      drawer.removeAttribute('inert');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('sy-nav-open', 'sy-nav-locked');
      mobileOpen?.setAttribute('aria-expanded', 'true');
      mobileSearchOpen?.setAttribute('aria-expanded', 'true');
      window.setTimeout(() => {
        const focusTarget = focusSearch ? mobileSearch?.querySelector('[data-sy-search-input]') : mobileClose;
        focusTarget?.focus({ preventScroll: true });
      }, 40);
    };

    const closeDrawer = (restoreFocus = true) => {
      if (!drawer || drawer.getAttribute('aria-hidden') === 'true') return;
      drawer.setAttribute('aria-hidden', 'true');
      drawer.inert = true;
      drawer.setAttribute('inert', '');
      document.body.classList.remove('sy-nav-open', 'sy-nav-locked');
      mobileOpen?.setAttribute('aria-expanded', 'false');
      mobileSearchOpen?.setAttribute('aria-expanded', 'false');
      if (restoreFocus && drawerOpener instanceof HTMLElement) drawerOpener.focus();
    };

    mobileOpen?.addEventListener('click', () => openDrawer(mobileOpen, false));
    mobileSearchOpen?.addEventListener('click', () => openDrawer(mobileSearchOpen, true));
    mobileClose?.addEventListener('click', () => closeDrawer(true));
    overlay?.addEventListener('click', () => closeDrawer(true));

    drawer?.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => closeDrawer(false)));
    drawer?.querySelectorAll('[data-sy-accordion]').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.nextElementSibling;
        const willOpen = button.getAttribute('aria-expanded') !== 'true';
        if (button.classList.contains('sy-mobile-world__button') && willOpen) {
          drawer.querySelectorAll('.sy-mobile-world__button[aria-expanded="true"]').forEach((other) => {
            if (other === button) return;
            other.setAttribute('aria-expanded', 'false');
            other.nextElementSibling?.classList.remove('is-open');
          });
        }
        button.setAttribute('aria-expanded', String(willOpen));
        panel?.classList.toggle('is-open', willOpen);
      });
    });

    drawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer(true);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...drawer.querySelectorAll(SELECTOR_FOCUSABLE)].filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('pointerdown', (event) => {
      if (!header.contains(event.target) && !panels.some((panel) => panel.contains(event.target))) closeMenus();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeMenus();
        closeDrawer(true);
      }
    });

    window.addEventListener('resize', () => {
      closeMenus();
      if (window.innerWidth >= 1180) closeDrawer(false);
    });
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-suityou-header]').forEach(initializeHeader);
  };

  initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
